(function() {
  'use strict';

  if (window._irConsentManagerLoaded) return;
  window._irConsentManagerLoaded = true;

  console.log('[IR Consent] Initializing consent manager');

  var STORAGE_KEY = 'ir_consent';
  var REGION_CACHE_KEY = 'ir_consent_region';
  var MAX_AGE_MS = 180 * 24 * 3600 * 1000;
  var REGION_TTL_MS = 24 * 3600 * 1000;
  var GEO_API_URL = 'https://vercel-geo-api-xi.vercel.app/api/is-non-ca-state';
  // If Shopify's privacy API has not loaded by INIT_MS, or does not confirm a consent change within CALLBACK_MS,
  // tracking is allowed for the page (nothing saved) rather than left off. Site functions never wait on either.
  var INIT_MS = 5000;
  var CALLBACK_MS = 3000;
  var defined = false;
  try { defined = typeof localStorage !== 'undefined' && !!localStorage; } catch (e) {}

  function log() {
    console.log.apply(console, ['[IR Consent]'].concat([].slice.call(arguments)));
  }

  function normalizeRegion(region) {
    return String(region || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function getCachedRegion() {
    if (!defined) return '';

    try {
      var raw = localStorage.getItem(REGION_CACHE_KEY);

      if (!raw) {
        log('No cached region found');
        return '';
      }

      var data = JSON.parse(raw);

      if (!data || !data.region || !data.ts) {
        log('Invalid cached region data');
        return '';
      }

      if (Date.now() - data.ts > REGION_TTL_MS) {
        log('Cached region expired:', data.region);
        localStorage.removeItem(REGION_CACHE_KEY);
        return '';
      }

      log('Using cached region:', data.region);

      return normalizeRegion(data.region);

    } catch (e) {
      console.error('[IR Consent] Failed reading cached region', e);

      try {
        localStorage.removeItem(REGION_CACHE_KEY);
      } catch (err) {}

      return '';
    }
  }

  function setCachedRegion(region) {
    var n = normalizeRegion(region);

    if (!n || !defined) return;

    try {
      localStorage.setItem(REGION_CACHE_KEY, JSON.stringify({
        region: n,
        ts: Date.now()
      }));

      log('Saved cached region:', n);

    } catch (e) {
      console.error('[IR Consent] Failed saving region', e);
    }
  }

  function shouldRequireConsent(region) {
    var n = normalizeRegion(region);

    var EU_COUNTRIES = [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
      'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
    ];

    if (n === 'USCA') {
      log('California detected -> consent required');
      return true;
    }

    if (EU_COUNTRIES.indexOf(n) !== -1) {
      log('EU region detected -> consent required');
      return true;
    }

    log('Consent bypass allowed for region:', n);

    return false;
  }

  function shouldBypassConsent(region) {
    return !shouldRequireConsent(region);
  }

  function regionFromGeo(country, region) {
    var c = normalizeRegion(country);
    var r = normalizeRegion(region);

    if (c === 'US' && r && r.length === 2) {
      return 'US' + r;
    }

    return c || '';
  }

  function getShopifyRegion() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;

      if (cp && typeof cp.getRegion === 'function') {
        var r = cp.getRegion();

        log('Shopify region response:', r);

        // getRegion() returns a string such as 'USCA', 'DE' or 'DEBY'. A US answer without a state cannot rule
        // out California, so the geo API decides then.
        if (typeof r === 'string') {
          var s = normalizeRegion(r);
          if (s.indexOf('US') === 0) return s.length === 4 ? s : '';
          return s.slice(0, 2);
        }

        if (r && r.stateProv) {
          return regionFromGeo(r.country || 'US', r.stateProv);
        }

        // A US answer without a state cannot rule out California either.
        if (r && r.country && normalizeRegion(r.country) !== 'US') {
          return normalizeRegion(r.country);
        }
      }
    } catch (e) {
      console.error('[IR Consent] Shopify region error', e);
    }

    return '';
  }

  function resolveRegion(cp) {
    var cached = getCachedRegion();

    if (cached) {
      return Promise.resolve(cached);
    }

    var shopifyRegion = getShopifyRegion();

    if (shopifyRegion) {
      setCachedRegion(shopifyRegion);

      log('Resolved region via Shopify:', shopifyRegion);

      return Promise.resolve(shopifyRegion);
    }

    return new Promise(function(resolve) {

      log('Falling back to geo API');

      var done = false;

      function finish(region) {
        if (done) return;

        done = true;

        var n = normalizeRegion(region);

        if (n) {
          setCachedRegion(n);
        }

        log('Final resolved region:', n);

        resolve(n);
      }

      var timeout = setTimeout(function() {
        log('Geo API timeout');
        finish('');
      }, 1500);

      if (!window.fetch) {
        clearTimeout(timeout);
        finish('');
        return;
      }

      window.fetch(GEO_API_URL, { method: 'GET' })
        .then(function(res) {
          return res.ok ? res.json() : Promise.reject();
        })
        .then(function(data) {

          log('Geo API response:', data);

          clearTimeout(timeout);

          finish(regionFromGeo(
            data && data.country,
            data && data.region
          ));
        })
        .catch(function(err) {

          console.error('[IR Consent] Geo API failed', err);

          clearTimeout(timeout);
          finish('');
        });
    });
  }

  function getSaved() {
    if (!defined) return null;

    try {
      var raw = localStorage.getItem(STORAGE_KEY);

      if (!raw) {
        log('No saved consent found');
        return null;
      }

      var d = JSON.parse(raw);

      if (d.ts && Date.now() - d.ts > MAX_AGE_MS) {
        log('Saved consent expired');

        localStorage.removeItem(STORAGE_KEY);

        return null;
      }

      log('Loaded saved consent:', d);

      return d;

    } catch (e) {

      console.error('[IR Consent] Failed loading consent', e);

      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {}

      return null;
    }
  }

  function saveLocal(analytics, marketing) {
    if (!defined) return;

    try {

      var payload = {
        a: !!analytics,
        m: !!marketing,
        ts: Date.now()
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

      log('Saved consent locally:', payload);

    } catch (e) {
      console.error('[IR Consent] Failed saving consent', e);
    }
  }

  function getPayload() {
    var el = document.getElementById('ir-tracking-payload');

    if (!el) {
      log('No tracking payload found');
      return null;
    }

    try {
      var parsed = JSON.parse(el.textContent);

      log('Tracking payload loaded');

      return parsed;

    } catch (e) {
      console.error('[IR Consent] Invalid tracking payload JSON', e);
      return null;
    }
  }

  function injectHtml(html, target) {
    if (!html || !target) return;

    log('Injecting HTML into', target.tagName);

    var tmp = document.createElement('div');
    tmp.innerHTML = html;

    while (tmp.firstChild) {
      var node = tmp.firstChild;

      if (node.nodeType === 1 && node.nodeName === 'SCRIPT') {

        var s = document.createElement('script');

        for (var i = 0; i < node.attributes.length; i++) {
          s.setAttribute(
            node.attributes[i].name,
            node.attributes[i].value
          );
        }

        s.textContent = node.textContent;

        log('Injecting script:', s.src || 'inline script');

        tmp.removeChild(node);

        target.appendChild(s);

      } else {
        target.appendChild(node);
      }
    }
  }

  var _injected = false;
  var _pageViewFired = false;

  function injectCustomScripts(payload) {

    if (_injected) {
      log('Scripts already injected');
      return;
    }

    if (!payload) {
      log('No payload available for script injection');
      return;
    }

    _injected = true;

    log('Injecting tracking scripts');

    injectHtml(payload.gtmHead, document.head);
    injectHtml(payload.gtmBody, document.body);

    window.irTrackingAllowed = true;

    if (typeof window.irRunFbLinkScript === 'function') {
      log('Running Facebook link script');
      window.irRunFbLinkScript();
    }
  }

  function applyConsent(payload, analytics, marketing, opts) {

    opts = opts || {};

    analytics = !!analytics;
    marketing = !!marketing;

    log('Applying consent', {
      analytics: analytics,
      marketing: marketing,
      opts: opts
    });

    var hasConsent = analytics || marketing;

    if (opts.persist !== false) {
      saveLocal(analytics, marketing);
    }

    if (typeof window._irWriteConsentCookie === 'function') {
      log('Writing consent cookie');
      window._irWriteConsentCookie(analytics, marketing);
    }

    // Google tags follow the same choice (the head script set the page's default).
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('consent', 'update', {
          ad_storage: marketing ? 'granted' : 'denied',
          ad_user_data: marketing ? 'granted' : 'denied',
          ad_personalization: marketing ? 'granted' : 'denied',
          analytics_storage: analytics ? 'granted' : 'denied',
          functionality_storage: analytics ? 'granted' : 'denied',
          personalization_storage: analytics ? 'granted' : 'denied'
        });
      } catch (e) {
        console.error('[IR Consent] Failed updating Google consent', e);
      }
    }

    if (hasConsent) {
      if (typeof window._irUnblockTracking === 'function') {
        log('Unblocking tracking');
        window._irUnblockTracking();
      }
    } else if (typeof window._irBlockTracking === 'function') {
      log('Blocking tracking');
      window._irBlockTracking();
    }

    if (typeof window._irUnblockPixels === 'function') {
      if (hasConsent) {
        log('Unblocking pixels');
        window._irUnblockPixels();
      }
    }

    if (hasConsent) {

      if (opts.inject !== false) {
        injectCustomScripts(payload);
      }

      if (opts.firePageView && !_pageViewFired) {
        _pageViewFired = true;

        try {

          if (typeof window.gtag === 'function') {

            log('Firing page_view');

            window.gtag('event', 'page_view');
          }

        } catch (e) {
          console.error('[IR Consent] Failed page_view', e);
        }
      }

    } else {

      log('Consent denied -> deleting tracking cookies');

      if (typeof deleteTrackingCookies === 'function') {
        deleteTrackingCookies();
        deleteTrackingCookies();
      }
    }

    if (opts.dispatchChanged !== false) {

      log('Dispatching irConsentChanged');

      document.dispatchEvent(
        new CustomEvent('irConsentChanged')
      );
    }
  }

  var _consentOp = 0; // the latest consent change; a late answer to an older one is ignored

  // Calls fn once: when Shopify confirms the change, or after CALLBACK_MS if it never does. Not at all if a newer
  // change was made meanwhile (a stalled saved choice must not undo the shopper's Accept).
  function setShopifyConsent(cp, analytics, marketing, fn) {
    var op = ++_consentOp;
    var ran = false;

    function once() {
      if (ran) return;
      ran = true;
      if (op !== _consentOp) {
        log('Ignoring the answer to an older consent change');
        return;
      }
      fn();
    }

    setTimeout(function() {
      if (!ran) {
        console.error('[IR Consent] Shopify did not confirm the consent change; continuing');
        once();
      }
    }, CALLBACK_MS);

    try {
      cp.setTrackingConsent({
        analytics: analytics,
        marketing: marketing,
        preferences: analytics,
        sale_of_data: marketing
      }, once);
    } catch (e) {
      console.error('[IR Consent] setTrackingConsent failed', e);
      once();
    }
  }

  var _regionResolved = false;
  var _failedOpen = false;

  // Shopify's privacy API or this script failed: apply the saved choice, or allow tracking for this page without
  // saving anything. If Shopify's API arrives later, this page stays as it is (see initWithCustomerPrivacy).
  function failOpen(payload, reason, force) {
    if (_failedOpen || (_regionResolved && !force)) return;
    _failedOpen = true;

    console.error('[IR Consent] ' + reason + ' -> tracking allowed for this page');

    var saved = getSaved();
    var analytics = saved ? !!saved.a : true;
    var marketing = saved ? !!saved.m : true;

    applyConsent(payload, analytics, marketing, {
      persist: false,
      firePageView: analytics || marketing
    });

    if (!saved) window.irConsentBypass = true;
    window.irBannerState = 'fab';

    document.dispatchEvent(
      new CustomEvent('irConsentReady', {
        detail: {
          state: 'fab',
          bypass: !saved,
          failOpen: true
        }
      })
    );
  }

  function initWithCustomerPrivacy(cp, payload) {

    log('Initializing with Shopify customer privacy');

    resolveRegion(cp).then(function(region) {

      _regionResolved = true;
      window.irConsentGeoRegion = normalizeRegion(region) || '';
      // A page that already went ahead without Shopify (failOpen) keeps its bypass flag; see below.
      if (!_failedOpen) window.irConsentBypass = shouldBypassConsent(window.irConsentGeoRegion);

     log('Region resolved:', {
  raw: region,
  normalized: window.irConsentGeoRegion,
  bypass: window.irConsentBypass
});
      window.irSetConsent = function(analytics, marketing) {

        log('User updated consent', {
          analytics: analytics,
          marketing: marketing
        });

        analytics = !!analytics;
        marketing = !!marketing;

        applyConsent(payload, analytics, marketing, {
          inject: false,
          dispatchChanged: false
        });

        setShopifyConsent(cp, analytics, marketing, function() {

          log('Shopify tracking consent updated');

          applyConsent(payload, analytics, marketing, {
            persist: true,
            firePageView: analytics || marketing
          });

          window.irConsentBypass = false;
          window.irBannerState = 'fab';

          document.dispatchEvent(
            new CustomEvent('irConsentReady', {
              detail: {
                state: 'fab',
                changedByUser: true
              }
            })
          );
        });
      };

      // Shopify's privacy API arrived after this page went ahead without it: leave the page as it is (tags already
      // loaded) rather than block it halfway. The region is cached, so the next page applies it; choices made in
      // the cookie settings from now on reach Shopify.
      if (_failedOpen) {
        log('Shopify privacy API arrived late; region applies from the next page');
        return;
      }

      var saved = getSaved();

      if (saved) {

        log('Applying saved consent');

        setShopifyConsent(cp, !!saved.a, !!saved.m, function() {

          applyConsent(payload, saved.a, saved.m, {
            persist: false,
            firePageView: saved.a || saved.m
          });

          window.irBannerState = 'fab';

          document.dispatchEvent(
            new CustomEvent('irConsentReady', {
              detail: {
                state: 'fab',
                saved: true,
                region: window.irConsentGeoRegion
              }
            })
          );
        });

        return;
      }

      if (window.irConsentBypass) {

        log('Bypassing consent and enabling tracking immediately');

        setShopifyConsent(cp, true, true, function() {

          // An unknown region (lookup failed) is allowed for this page but not saved.
          applyConsent(payload, true, true, {
            persist: !!window.irConsentGeoRegion,
            firePageView: true
          });

          window.irBannerState = 'fab';

          document.dispatchEvent(
            new CustomEvent('irConsentReady', {
              detail: {
                state: 'fab',
                bypass: true
              }
            })
          );
        });

        return;
      }

      log('Consent required -> showing banner');

      if (typeof window._irBlockTracking === 'function') window._irBlockTracking();

      // Counted as a consent change, so a later Accept supersedes it.
      setShopifyConsent(cp, false, false, function() {});

      window.irBannerState = 'banner';

      document.dispatchEvent(
        new CustomEvent('irConsentReady', {
          detail: {
            state: 'banner',
            bypass: false,
            region: window.irConsentGeoRegion
          }
        })
      );
    }).catch(function(e) {
      console.error('[IR Consent] Consent setup failed', e);
      failOpen(payload, 'Consent setup failed', true);
    });
  }

  function init() {

    log('Starting initialization');

    var payload = getPayload();

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      failOpen(payload, 'Shopify privacy API unavailable');
      return;
    }

    setTimeout(function() {
      failOpen(payload, 'Shopify privacy API did not load in time');
    }, INIT_MS);

    if (window.Shopify.customerPrivacy) {

      log('Shopify customer privacy already loaded');

      initWithCustomerPrivacy(
        window.Shopify.customerPrivacy,
        payload
      );

      return;
    }

    log('Loading Shopify consent-tracking-api');

    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1'
      }
    ], function(err) {

      if (err) {
        console.error('[IR Consent] Failed loading Shopify feature', err);
        failOpen(payload, 'Shopify privacy API failed to load');
        return;
      }

      var cp = window.Shopify.customerPrivacy;

      if (!cp) {
        failOpen(payload, 'Shopify customer privacy unavailable');
        return;
      }

      initWithCustomerPrivacy(cp, payload);
    });
  }

  function start() {
    try {
      init();
    } catch (e) {
      console.error('[IR Consent] Initialization failed', e);
      failOpen(getPayload(), 'Initialization failed');
    }
  }

  // consent-gate-bootstrap.js allows tracking for the page if this file never gets this far.
  window._irConsentManagerStarted = true;

  if (document.readyState === 'loading') {

    log('Waiting for DOMContentLoaded');

    document.addEventListener('DOMContentLoaded', start);

  } else {

    start();
  }

})();